/**
 * Solar Context
 * RoofMapper v3 - Solar API State Management
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { solarService } from '../services/solarService';
import {
  BuildingInsights,
  DataLayers,
  LatLng,
  RoofPolygon,
  CalibrationOffset,
  SolarContextType,
  CacheInfo,
} from '../types/solar';

const SolarContext = createContext<SolarContextType | undefined>(undefined);

interface SolarProviderProps {
  children: ReactNode;
}

export const SolarProvider: React.FC<SolarProviderProps> = ({ children }) => {
  // Solar API data
  const [buildingInsights, setBuildingInsights] = useState<BuildingInsights | null>(null);
  const [dataLayers, setDataLayers] = useState<DataLayers | null>(null);
  const [cacheInfo, setCacheInfo] = useState<CacheInfo | null>(null);

  // Processed data
  const [roofPolygons, setRoofPolygons] = useState<RoofPolygon[]>([]);
  const [selectedSegment, setSelectedSegment] = useState<number | null>(null);

  // Calibration
  const [overlayOffset, setOverlayOffset] = useState<CalibrationOffset>({ x: 0, y: 0 });

  // State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Human-readable phase shown next to the spinner so the user knows the
  // request is alive. We don't have real server-side progress (the call
  // is a single HTTP request), so we time-step labels that match the
  // typical backend phases (cache → Google Solar → GeoTIFF download).
  const [loadingStage, setLoadingStage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchSolarData: (
    position: LatLng,
    options?: { cacheRadius?: number; force?: boolean }
  ) => Promise<void> = useCallback(async (
    position: LatLng,
    options?: { cacheRadius?: number; force?: boolean }
  ): Promise<void> => {
    const startTime = performance.now();

    // Schedule label updates so the user sees the request advance even on
    // a slow cache miss. Times are tuned to the typical backend phases.
    setLoadingStage('Consultando datos solares guardados…');
    const stageTimers: number[] = [
      window.setTimeout(() => setLoadingStage('Consultando Google Solar API…'), 1500),
      window.setTimeout(() => setLoadingStage('Descargando capas GeoTIFF…'), 4500),
      window.setTimeout(() => setLoadingStage('Procesando datos solares…'), 9000),
    ];

    try {
      setIsLoading(true);
      setError(null);


      const response = await solarService.getBuildingInsights(position, options);

      const elapsed = ((performance.now() - startTime) / 1000).toFixed(2);
      const cacheHit = response.cacheHit || false;
      const cacheStatus = response.cacheStatus || 'UNKNOWN';

      // Validate buildingInsights exists in response
      if (!response.buildingInsights) {
        console.error('╔════════════════════════════════════════════════════════════════════════════╗');
        console.error('║                    ❌ INVALID CACHE RESPONSE                               ║');
        console.error('╠════════════════════════════════════════════════════════════════════════════╣');
        console.error('║ buildingInsights is null or undefined in response');
        console.error('║ Cache ID:', response.cacheId || 'None');
        console.error('║ This cache entry may be corrupted or incomplete');
        console.error('╚════════════════════════════════════════════════════════════════════════════╝');

        // If we haven't already tried force fetch, retry with force=true to bypass corrupted cache
        if (!options?.force) {
          setIsLoading(false);
          return fetchSolarData(position, { ...options, force: true });
        }

        throw new Error('Cache returned invalid data (buildingInsights is null). The cache entry may be corrupted.');
      }


      setBuildingInsights(response.buildingInsights);
      setDataLayers(response.dataLayers);

      // Store cache info with data quality and availability
      setCacheInfo({
        cacheHit: cacheHit,
        cacheStatus: cacheStatus,
        cacheId: response.cacheId,
        uploadStatus: response.uploadStatus,
        geotiffCacheId: response.geotiffCacheId,
        timestamp: Date.now(),
        dataQuality: response.buildingInsights?.imageryQuality || 'UNKNOWN',
        solarAvailable: true,  // Successfully retrieved solar data
      });
    } catch (err: any) {
      const elapsed = ((performance.now() - startTime) / 1000).toFixed(2);
      const errorMessage = err.response?.data?.error || 'Failed to fetch solar data';


      setError(errorMessage);
      console.error('Solar API error details:', err);
      throw new Error(errorMessage);
    } finally {
      stageTimers.forEach((t) => window.clearTimeout(t));
      setLoadingStage(null);
      setIsLoading(false);
    }
  }, []);

  const clearSolarData = useCallback(() => {
    setBuildingInsights(null);
    setDataLayers(null);
    setCacheInfo(null);
    setRoofPolygons([]);
    setSelectedSegment(null);
    setOverlayOffset({ x: 0, y: 0 });
    setError(null);
  }, []);

  const selectSegment = useCallback((index: number | null) => {
    setSelectedSegment(index);
  }, []);

  const value: SolarContextType = {
    // Solar API data
    buildingInsights,
    dataLayers,
    cacheInfo,

    // Processed data
    roofPolygons,
    selectedSegment,

    // Actions
    fetchSolarData,
    clearSolarData,
    selectSegment,

    // Calibration
    overlayOffset,
    setOverlayOffset,

    // State
    isLoading,
    loadingStage,
    error,
  };

  return <SolarContext.Provider value={value}>{children}</SolarContext.Provider>;
};

// Custom hook to use solar context
export const useSolar = (): SolarContextType => {
  const context = useContext(SolarContext);
  if (context === undefined) {
    throw new Error('useSolar must be used within a SolarProvider');
  }
  return context;
};

export default SolarContext;
